import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { profilesApi } from '../services/api';
import { useAuthStore } from '../stores/auth.store';

interface OnboardingGuardProps {
  children: ReactNode;
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { isAuthenticated } = useAuthStore();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: profilesApi.getMyProfile,
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Still loading profile
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // No profile yet — redirect to create
  if (!profile) {
    return <Navigate to="/profile/create" replace />;
  }

  return <>{children}</>;
}

// Calculate profile completeness (0-100)
export function getProfileCompleteness(profile: {
  currentLife?: string;
  lookingFor?: string;
  whatsImportant?: string;
  notLookingFor?: string;
} | null): number {
  if (!profile) return 0;

  let score = 0;
  if (profile.currentLife && profile.currentLife.length > 10) score += 30;
  if (profile.lookingFor && profile.lookingFor.length > 10) score += 30;
  if (profile.whatsImportant && profile.whatsImportant.length > 10) score += 25;
  if (profile.notLookingFor && profile.notLookingFor.length > 5) score += 15;

  return score;
}
